import { FC, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { supabase } from "@/lib/supabase";
import { selectAuthUser } from "@/store/selectors/auth";
import Button from "../Button/Button";

import styles from "./ApiKeysSection.module.css";

interface ApiKey {
    id: number;
    key_prefix: string;
    name: string | null;
    created_at: string;
    is_active: boolean;
}

const ApiKeysSection: FC = () => {
    const user = useSelector(selectAuthUser);
    const [keys, setKeys] = useState<ApiKey[]>([]);
    const [loading, setLoading] = useState(false);
    const [newKey, setNewKey] = useState<string | null>(null);
    const [keyName, setKeyName] = useState("");
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchKeys = useCallback(async () => {
        if (!user) return;
        const { data } = await supabase
            .from("api_keys")
            .select("id, key_prefix, name, created_at, is_active")
            .eq("is_active", true)
            .is("revoked_at", null)
            .order("created_at", { ascending: false });
        if (data) setKeys(data);
    }, [user]);

    useEffect(() => {
        fetchKeys();
    }, [fetchKeys]);

    const handleGenerate = async () => {
        setLoading(true);
        setError(null);
        try {
            const { data, error: rpcError } = await supabase.rpc("generate_api_key", {
                p_name: keyName.trim() || null,
            });
            if (rpcError) throw rpcError;
            setNewKey(data.key);
            setKeyName("");
            await fetchKeys();
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "Failed to generate key");
        } finally {
            setLoading(false);
        }
    };

    const handleRevoke = async (id: number) => {
        if (!window.confirm("Revoke this API key? This cannot be undone.")) return;
        await supabase
            .from("api_keys")
            .update({ is_active: false, revoked_at: new Date().toISOString() })
            .eq("id", id);
        await fetchKeys();
    };

    const handleCopy = async () => {
        if (!newKey) return;
        await navigator.clipboard.writeText(newKey);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleDismiss = () => {
        setNewKey(null);
        setCopied(false);
    };

    return (
        <div className={styles.section}>
            <p className={styles.section__description}>
                Use API keys to access your data programmatically via{" "}
                <code>api.aicarma.com</code>.
            </p>

            {/* New key banner */}
            {newKey && (
                <div className={styles.banner}>
                    <p className={styles.banner__warning}>
                        Please copy this key now — you won't be able to see it again.
                    </p>
                    <div className={styles.banner__key}>
                        <code>{newKey}</code>
                        <button
                            className={styles.banner__copy}
                            onClick={handleCopy}
                            type="button"
                        >
                            {copied ? "✓ Copied" : "Copy"}
                        </button>
                    </div>
                    <button
                        className={styles.banner__dismiss}
                        onClick={handleDismiss}
                        type="button"
                    >
                        I've saved this key
                    </button>
                </div>
            )}

            {/* Generate form */}
            {!newKey && (
                <div className={styles.generate}>
                    <input
                        className={styles.generate__input}
                        type="text"
                        placeholder="Key name (optional)"
                        value={keyName}
                        onChange={(e) => setKeyName(e.target.value)}
                        maxLength={50}
                    />
                    <Button
                        label={loading ? "Generating..." : "Generate API Key"}
                        typeStyle="fill-dark"
                        size="md"
                        onClick={handleGenerate}
                        disabled={loading}
                        type="button"
                    />
                </div>
            )}

            {error && <p className={styles.error}>{error}</p>}

            {/* Keys list */}
            {keys.length > 0 && (
                <div className={styles.list}>
                    <div className={styles.list__header}>
                        <span>Key</span>
                        <span>Name</span>
                        <span>Created</span>
                        <span />
                    </div>
                    {keys.map((k) => (
                        <div key={k.id} className={styles.list__row}>
                            <code className={styles.list__prefix}>{k.key_prefix}...</code>
                            <span className={styles.list__name}>{k.name || "—"}</span>
                            <span className={styles.list__date}>
                                {new Date(k.created_at).toLocaleDateString()}
                            </span>
                            <button
                                className={styles.list__revoke}
                                onClick={() => handleRevoke(k.id)}
                                type="button"
                            >
                                Revoke
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {keys.length === 0 && !newKey && (
                <p className={styles.empty}>No API keys yet. Generate one to get started.</p>
            )}
        </div>
    );
};

export default ApiKeysSection;
